<template>
  <div>
    <v-btn @click="dialog" color="yellow" class="mr- ml-" icon
      ><v-icon>mdi-cash-multiple</v-icon></v-btn
    >
    <v-dialog v-model="dialog" width="1100" persistent>
      <v-card>
        <ModalHeader
          title="Add Payment"
          icon="mdi-cash"
          :subtitle="invoice.invoice_code"
          @close="dialog = false"
        >
          <template v-slot:actions>
            <v-chip v-if="isVatInclusive" color="warning" small class="mr-2">
              <v-icon small left>mdi-receipt-text</v-icon>
              VAT-Inclusive
            </v-chip>
          </template>
        </ModalHeader>
        <v-card-text class="pa-4">
          <v-row>
            <!-- ===== Payment form ===== -->
            <v-col cols="12" md="6">
              <div class="d-flex">
                <h4 class="mr-5 mb-4">Invoice Amount: {{ invoice.amount }}</h4>
                <h4>
                  Invoice Discount:
                  {{ invoice.discount }}
                </h4>
              </div>

              <v-divider class="mb-4"></v-divider>

              <!-- Banner shown when an advanced downpayment is being applied -->
              <v-alert
                v-if="selectedDp"
                dense
                text
                type="info"
                class="mb-3"
                border="left"
              >
                <div class="d-flex align-center">
                  <div>
                    Applying down payment
                    <strong>#{{ selectedDp.reference_number }}</strong>
                    (Available: ₱{{
                      Number(selectedDp.available_amount).toLocaleString()
                    }})
                    <br />
                    Payment method set to
                    <strong>Advanced Downpayments</strong>.
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn x-small text color="error" @click="clearDownpayment"
                    >Clear</v-btn
                  >
                </div>
              </v-alert>

              <v-text-field
                outlined
                dense
                v-model="total_amount"
                label="Total Payable Amount"
                prepend-icon="mdi-cash-multiple"
                readonly
              ></v-text-field>
              <v-text-field
                outlined
                dense
                :label="selectedDp ? 'Amount to Apply' : 'Cash'"
                prepend-icon="mdi-cash"
                v-model="form.amount_paid"
                @input="check_cash_input"
              ></v-text-field>

              <v-text-field
                outlined
                dense
                label="Balance"
                prepend-icon="mdi-hand-coin"
                :value="change"
                readonly
              ></v-text-field>
              <v-select
                outlined
                dense
                :items="chart_of_accounts"
                item-text="account_title"
                item-value="id"
                prepend-icon=""
                label="Paid To"
                v-model="form.chart_of_account_id"
                :disabled="!!selectedDp"
                :hint="selectedDp ? 'Locked to Advanced Downpayments' : ''"
                persistent-hint
              ></v-select>
              <v-row>
                <v-col cols="5">
                  <span class="mr-2">Payment Date: </span>
                  <input type="date" v-model="form.payment_date" class="mb-3"
                /></v-col>

                <v-col>
                  <v-text-field
                    outlined
                    dense
                    :label="isVatInclusive ? 'OR Number *' : 'OR Number'"
                    v-model="form.or_number"
                    :rules="
                      isVatInclusive
                        ? [
                            (v) =>
                              !!v ||
                              'OR Number is required for VAT-inclusive invoices',
                          ]
                        : []
                    "
                    :required="isVatInclusive"
                    :hint="
                      isVatInclusive
                        ? 'Required for VAT-inclusive invoices'
                        : ''
                    "
                    persistent-hint
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-select
                outlined
                dense
                :items="payment_status"
                prepend-icon=""
                label="Payment Status"
                v-model="form.invoice_status"
              ></v-select>
            </v-col>

            <!-- ===== Advanced down payments ===== -->
            <v-col cols="12" md="6">
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-1" color="primary"
                  >mdi-cash-refund</v-icon
                >
                <h4 class="mb-0">Available Down Payments</h4>
                <v-spacer></v-spacer>
                <v-btn x-small icon @click="get_customer_downpayments">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <v-progress-linear
                v-if="loadingDps"
                indeterminate
                color="primary"
              ></v-progress-linear>

              <v-simple-table dense v-if="downpayments.length > 0">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Ref</th>
                      <th class="text-right">Amount</th>
                      <th class="text-right">Available</th>
                      <th class="text-center">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="dp in downpayments" :key="dp.id">
                      <td>{{ dp.reference_number }}</td>
                      <td class="text-right">
                        {{ Number(dp.amount_paid).toLocaleString() }}
                      </td>
                      <td class="text-right">
                        {{ Number(dp.available_amount).toLocaleString() }}
                      </td>
                      <td class="text-center">
                        <v-chip
                          x-small
                          :color="
                            dp.status === 'Transacted'
                              ? 'green'
                              : dp.status === 'Partial'
                              ? 'orange'
                              : 'blue-grey'
                          "
                          dark
                          >{{ dp.status || "Pending" }}</v-chip
                        >
                      </td>
                      <td>
                        <v-btn
                          x-small
                          color="primary"
                          :disabled="
                            dp.available_amount <= 0 ||
                            (selectedDp && selectedDp.id === dp.id)
                          "
                          @click="applyDownpayment(dp)"
                        >
                          {{
                            selectedDp && selectedDp.id === dp.id
                              ? "Applied"
                              : "Use"
                          }}
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <p v-else-if="!loadingDps" class="grey--text caption mt-2">
                No down payments found for this customer.
              </p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" text @click="submit" :loading="loading"
            >Submit</v-btn
          >
          <v-btn text @click="$emit('close')">Close</v-btn>
        </v-card-actions>
      </v-card></v-dialog
    >
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import moment from "moment";
export default {
  props: ["invoice"],
  data() {
    return {
      loading: false,
      payment_status: ["Paid", "With Balance", "Waiting for Payment"],
      tax_code: ["Tax Exempt", "VAT", "Non-Vat"],
      dialog: true,
      total_amount: this.invoice.balance,
      // Advanced down payments
      downpayments: [],
      loadingDps: false,
      selectedDp: null,
      form: {
        invoice_id: this.invoice.id,
        invoice_status: "Paid",
        amount_paid: 0,
        total_payable_amount: this.invoice.balance,
        balance: "",
        chart_of_account_id: 5,
        payment_date: "",
        tax_code: "Tax Exempt",
        or_number: 0,
        downpayment_id: null,
      },
    };
  },
  methods: {
    check_cash_input() {
      this.form.invoice_status =
        this.form.amount_paid != this.total_amount ? "With Balance" : "Paid";
    },

    ...mapActions({
      add_payment: "payment/add_payment",
      set_notification: "notification/set_notification",
      get_chart_of_accounts: "chart_of_account/get_chart_of_accounts",
    }),

    async get_customer_downpayments() {
      const customerId = this.invoice.customer_id;
      if (!customerId) return;
      this.loadingDps = true;
      try {
        const response = await axios.get("/downpayment/" + customerId);
        this.downpayments = response.data;
      } catch (e) {
        this.downpayments = [];
      }
      this.loadingDps = false;
    },

    applyDownpayment(dp) {
      const available = Number(dp.available_amount);
      if (available <= 0) return;
      this.selectedDp = dp;
      this.form.downpayment_id = dp.id;
      // Switch the payment method to Advanced Downpayments.
      this.form.chart_of_account_id = this.advancedDpAccountId;
      // Default to applying whichever is smaller: the remaining balance or
      // the amount still available on this down payment. The field stays
      // editable so a partial amount can be entered.
      const applied = Math.min(available, Number(this.total_amount));
      this.form.amount_paid = applied;
      this.check_cash_input();
    },

    clearDownpayment() {
      this.selectedDp = null;
      this.form.downpayment_id = null;
      this.form.chart_of_account_id = 5;
      this.form.amount_paid = this.total_amount;
      this.check_cash_input();
    },

    async submit() {
      // When applying a down payment, the amount cannot exceed what is
      // still available on it.
      if (this.selectedDp) {
        if (Number(this.form.amount_paid) <= 0) {
          this.$alert({
            title: "Validation",
            message: "Amount to apply must be greater than zero.",
            color: "warning",
            icon: "mdi-alert",
          });
          return;
        }
        if (
          Number(this.form.amount_paid) >
          Number(this.selectedDp.available_amount)
        ) {
          this.$alert({
            title: "Validation",
            message:
              "Amount to apply exceeds the available down payment (₱" +
              Number(this.selectedDp.available_amount).toLocaleString() +
              ").",
            color: "warning",
            icon: "mdi-alert",
          });
          return;
        }
      }
      // set balance
      this.form.balance =
        this.form.total_payable_amount - this.form.amount_paid;
      // check fields
      this.loading = true;
      if (this.form.payment_date == "") {
        this.loading = false;
        this.$alert({
          title: "Validation",
          message: "Please check all fields!",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      // Check OR Number for VAT-inclusive invoices
      if (
        this.isVatInclusive &&
        (!this.form.or_number || this.form.or_number == 0)
      ) {
        this.loading = false;
        this.$alert({
          title: "Validation",
          message: "OR Number is required for VAT-inclusive invoices!",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      await this.add_payment(this.form)
        .then(() => {
          this.$alert({
            title: "Success",
            message: this.selectedDp
              ? "Down payment applied as payment."
              : "Payment inserted",
            color: "success",
            icon: "mdi-check-circle",
          });
          this.$emit("close");
        })
        .catch((e) => {
          this.loading = false;
          this.$alert({
            title: "Error",
            message: String(e),
            color: "error",
            icon: "mdi-alert-circle",
          });
        });
      this.loading = false;
    },
  },
  created() {
    this.form.payment_date = moment().format("Y-MM-DD");
    this.form.amount_paid = this.total_amount;
    if (this.chart_of_accounts.length <= 0) {
      this.get_chart_of_accounts();
    }
    this.get_customer_downpayments();
  },
  computed: {
    change() {
      let x;
      if (this.amount_paid == 0) x = 0;
      else x = this.total_amount - this.form.amount_paid;
      return x;
    },
    isVatInclusive() {
      return this.invoice && this.invoice.vat_type === "inclusive";
    },
    // Resolve the "Advanced Downpayments" chart of account, falling back to
    // the known account id (86) if it cannot be matched by title.
    advancedDpAccountId() {
      const match = (this.chart_of_accounts || []).find(
        (a) =>
          a.account_title &&
          a.account_title.toLowerCase().includes("advanced downpayment")
      );
      return match ? match.id : 86;
    },
    ...mapGetters({
      chart_of_accounts: "chart_of_account/chart_of_accounts",
    }),
  },
};
</script>

<style lang="scss" scoped></style>
